import { Link, Outlet } from 'react-router';
import { MotionConfig } from 'framer-motion';
import { copy } from './lib/copy';
import { AuthProvider } from './components/AuthProvider';
import { Wordmark } from './components/Wordmark';
import { AccountMenu } from './components/AccountMenu';
import { Button } from './components/ui/Button';
import { useAuth } from './lib/auth';

export function App() {
  return (
    <AuthProvider>
      {/* reducedMotion="user" → framer respects prefers-reduced-motion app-wide:
          transform/layout animations are skipped (opacity still fades), the JS
          counterpart to the global CSS reduced-motion rule (§6.4). */}
      <MotionConfig reducedMotion="user">
        <div className="relative min-h-screen flex flex-col">
          <Header />

          <main className="relative z-[var(--z-content)] flex-1 flex flex-col">
            <Outlet />
          </main>

          <Footer />
        </div>
      </MotionConfig>
    </AuthProvider>
  );
}

function Footer() {
  return (
    <footer className="relative z-[var(--z-content)] px-6 py-8 text-center text-xs text-ink-faint">
      <nav className="mb-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
        <Link to="/pricing" className="transition-colors hover:text-ink">
          Pricing
        </Link>
        <Link to="/terms" className="transition-colors hover:text-ink">
          Terms
        </Link>
        <Link to="/privacy" className="transition-colors hover:text-ink">
          Privacy
        </Link>
        <Link to="/refunds" className="transition-colors hover:text-ink">
          Refunds
        </Link>
      </nav>
      <p>{copy.brand.footer}</p>
    </footer>
  );
}

/**
 * Sticky frosted header. Brand lockup (icon + wordmark) on the left — the
 * wordmark drops on very narrow screens (<360px), icon always shows. Account
 * actions collapse into a compact avatar menu instead of a cramped row of
 * text links (which wrapped badly on mobile).
 */
function Header() {
  const { session, loading, signIn, signOut } = useAuth();

  return (
    <header className="sticky top-0 z-[var(--z-sticky)] border-b border-border/60 bg-bg/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <Link
          to="/"
          aria-label={copy.brand.name}
          className="flex shrink-0 items-center gap-2.5 text-ink transition-colors hover:text-brand"
        >
          <img
            src="/wrappa-icon.png"
            alt=""
            width={32}
            height={32}
            className="h-8 w-8 rounded-[10px] shadow-sm"
          />
          {/* 2px optical nudge — the wordmark's cap height sits a touch high
              against the icon's visual centre. */}
          <Wordmark className="hidden h-5 w-auto translate-y-[2px] min-[360px]:block" />
        </Link>

        {loading ? (
          <div className="h-8 w-8" aria-hidden="true" />
        ) : session ? (
          <nav className="flex items-center gap-2 sm:gap-3">
            <Link to="/create">
              <Button size="sm">{copy.nav.create}</Button>
            </Link>
            <AccountMenu
              displayName={session.user.firstName ?? session.user.email}
              email={session.user.email}
              onSignOut={() => signOut('/')}
            />
          </nav>
        ) : (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => signIn(window.location.pathname + window.location.search)}
          >
            {copy.nav.signIn}
          </Button>
        )}
      </div>
    </header>
  );
}
